/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { Container, Typography, Chip, Stack } from "@mui/material";
import GlobalPricingTable from "../GlobalPricingTable/GlobalPricingTable";
import "./GlobalAdminMonetize.css";

function createData({ type, id, productId, price, trialPeriod, noOfPlans, lastModified, plans }) {
  return { type, id, productId, price, trialPeriod, noOfPlans, lastModified, plans };
}

function createDataPremium(productId, type, name, price, currencyCode, planId, lastUpdated) {
  return { productId, type, name, price, currencyCode, planId, lastUpdated };
}

function GlobalAdminMonetize() {
  const [inAppRows, setInAppRows] = React.useState([]);
  const [premiumRows, setPremiumRows] = React.useState([]);

  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );

  const extractPlansFromProducts = useCallback((products) => {
    let allPlans = [];
    products.forEach((product) => {
      if (product.oneTimePlan !== null) {
        allPlans.push({
          productId: product.product.id,
          name: product.oneTimePlan.name,
          price: product.oneTimePlan.basePrice.price,
          currencyCode: product.oneTimePlan.basePrice.currencyCode,
          planId: product.oneTimePlan.id,
          type: product.oneTimePlan.planType,
          lastUpdated: product.oneTimePlan.lastUpdated,
        });
      }
      if (product.recurringPlans.length > 0) {
        product.recurringPlans.forEach((element) => {
          allPlans.push({
            productId: product.product.id,
            name: element.name,
            price: element.basePrice.price,
            currencyCode: element.basePrice.currencyCode,
            planId: element.id,
            type: element.planType,
            lastUpdated: element.lastUpdated,
          });
        });
      }
    });
    return allPlans;
  }, []);

  const formTableWithData = useCallback((products) => {
    let allRows = [];
    products.forEach((product) => {
      if (product.oneTimePlan) {
        allRows.push(
          createData({
            type: "oneTime",
            id: product.product.id,
            productId: product.product.productId,
            price: product.oneTimePlan?.basePrice?.price.toFixed(2),
            trialPeriod: product.trialPrice?.trialPeriod,
            noOfPlans: 1,
            lastModified: product.product.lastUpdated,
            plans: [],
          })
        );
      } else {
        allRows.push(
          createData({
            type: "recurring",
            id: product.product.id,
            productId: product.product.productId,
            price: "-",
            trialPeriod: product.trialPrice?.trialPeriod,
            noOfPlans: product.recurringPlans.length,
            lastModified: product.product.lastUpdated,
            plans: product.recurringPlans,
          })
        );
      }
    });
    setInAppRows(allRows);
  }, []);

  const formTableWithPremiumData = useCallback((products) => {
    const allPlans = extractPlansFromProducts(products);
    const premiumRows = allPlans.map((plan) =>
      createDataPremium(
        plan.productId,
        plan.type,
        plan.name,
        plan.price,
        plan.currencyCode,
        plan.planId,
        plan.lastUpdated
      )
    );
    setPremiumRows(premiumRows);
  }, [extractPlansFromProducts]);

  useEffect(() => {
    if (submissionData?.isInAppPurchase) {
      // Use getAllInApp API for Global Admin
      APIManager.getAllInApp(
        submissionData.paymentSellerId,
        submissionData.binary.packageName,
        true // skipInvalidProducts
      ).then((json) => {
        if (json.length > 0) {
          formTableWithData(json);
        }
      });
    }
    if (submissionData?.isPremiumApp) {
      // Use getAllPremiumApps API for Global Admin
      APIManager.getAllPremiumApps(
        submissionData.paymentSellerId,
        submissionData.binary.packageName,
        true // skipInvalidProducts
      ).then((json) => {
        if (json.length > 0) {
          formTableWithPremiumData(json);
        }
      });
    }
  }, [submissionData, formTableWithData, formTableWithPremiumData]);

  const renderPricingSection = (
    title,
    label1,
    label2,
    tableType,
    data
  ) => (
    <>
      <Typography
        variant="button"
        gutterBottom
        sx={{
          display: "block",
          textTransform: "none",
          fontWeight: "bold",
          marginTop: title === "In-App Products Pricing" ? "2%" : "3%",
        }}
      >
        {title}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ marginTop: "1%" }}>
        <Chip label={label1} sx={{ fontWeight: "bold" }} />
        <Chip
          label={label2}
          variant="outlined"
          sx={{ border: "none", color: "#757575" }}
        />
      </Stack>
      <GlobalPricingTable listType={tableType} data={data} />
    </>
  );

  return (
    <Container maxWidth="lg" style={{ marginBottom: "16px" }}>
      <div>
        <div>
          <section
            id="monetizePricing"
            className="rollout"
            style={
              submissionData &&
              Object.keys(submissionData).length > 0 &&
              submissionData.status === "DRAFT"
                ? { marginBottom: "20px !important", width: "85%" }
                : { marginBottom: "20px !important" }
            }
          >
            <Typography
              sx={{
                "font-weight": "bold !important",
                "font-size": "20px !important",
                color: "rgba(18, 20, 21, 1) !important",
                "margin-right": "auto !important",
              }}
              className="uploadSignedApkText"
            >
              Monetize
            </Typography>
            {sessionStorage.getItem("idpLogin") !==
              "true" && (
                <>
                  {renderPricingSection(
                    "In-App Products Pricing",
                    "FREE",
                    "In-App Purchases",
                    "InApp",
                    inAppRows
                  )}
                  {renderPricingSection(
                    "App Pricing",
                    "PREMIUM",
                    "Premium Purchases",
                    "Premium",
                    premiumRows
                  )}
                </>
              )}
          </section>
        </div>
      </div>
    </Container>
  );
}

export default GlobalAdminMonetize;